/**
 * Trace — Core engine state recorder.
 *
 * Records every state transition through the core engine as a flat stream
 * of { time, runtime, ns, uid, key, value } entries.
 *
 * The trace is disabled by default. Call `trace.enable(runtime)` to start
 * recording, and `trace.setTransport(fn)` to ship entries to a consumer
 * (e.g., dev server via WebSocket or HTTP POST).
 */

import { nodes } from "./nodes.js";
import { store } from "./store.js";

const DEFAULT_BATCH_INTERVAL = 200;
const DEFAULT_BATCH_SIZE = 100;

class Trace {
  #enabled = false;
  #runtime = null;
  #transport = null;
  #buffer = [];
  #batchTimer = null;
  #batchInterval = DEFAULT_BATCH_INTERVAL;
  #batchSize = DEFAULT_BATCH_SIZE;

  /**
   * Enable trace recording.
   * @param {string} runtime - Runtime identifier (e.g., "playground", "backend")
   */
  enable(runtime) {
    this.#runtime = runtime;
    this.#enabled = true;
  }

  /** Disable trace recording and flush remaining entries. */
  disable() {
    this.#enabled = false;
    this.#flush();
  }

  /** @returns {boolean} Whether tracing is active */
  get isEnabled() {
    return this.#enabled;
  }

  /**
   * Set the transport function for shipping entries.
   * Called with an array of entries on each flush.
   * @param {function(Array): void} fn
   * @param {Object} [options]
   * @param {number} [options.batchInterval] - Flush interval in ms (default 200)
   * @param {number} [options.batchSize] - Max entries per flush (default 100)
   */
  setTransport(fn, options = {}) {
    this.#transport = fn;
    if (options.batchInterval != null) this.#batchInterval = options.batchInterval;
    if (options.batchSize != null) this.#batchSize = options.batchSize;
  }

  /**
   * Record a trace entry.
   * @param {string} ns - Component namespace (e.g., "ape:block")
   * @param {string} uid - Component uid
   * @param {string} key - What changed: property name, "create", "destroy", method name
   * @param {*} value - The payload
   */
  record(ns, uid, key, value) {
    if (!this.#enabled) return;

    this.#buffer.push({
      time: new Date().toISOString(),
      runtime: this.#runtime,
      ns: ns || null,
      uid,
      key,
      value,
    });

    // Flush if batch size reached
    if (this.#buffer.length >= this.#batchSize) {
      this.#flush();
      return;
    }

    // Start batch timer if not running
    if (!this.#batchTimer) {
      this.#batchTimer = setTimeout(() => {
        this.#batchTimer = null;
        this.#flush();
      }, this.#batchInterval);
    }
  }

  /**
   * Capture a snapshot of all live instances and the store.
   * Sends through the trace transport as a single entry with key "snapshot".
   */
  snapshot() {
    if (!this.#enabled) return;

    const entry = {
      time: new Date().toISOString(),
      runtime: this.#runtime,
      ns: null,
      uid: null,
      key: "snapshot",
      value: {
        tree: nodes.getTree(),
        store: JSON.parse(JSON.stringify(store.cache)),
      },
    };

    // Send immediately, bypassing the batch buffer
    if (this.#transport) {
      try {
        this.#transport([entry]);
      } catch {
        // Transport failure should never break the application
      }
    }
  }

  #flush() {
    if (this.#batchTimer) {
      clearTimeout(this.#batchTimer);
      this.#batchTimer = null;
    }

    if (this.#buffer.length === 0) return;

    const entries = this.#buffer;
    this.#buffer = [];

    if (this.#transport) {
      try {
        this.#transport(entries);
      } catch {
        // Transport failure should never break the application
      }
    }
  }
}

const trace = new Trace();
export { Trace, trace };
